<template>
  <div>
    <h2
      class="mt-0 tw-text-2xl tw-font-semibold tw-mb-2"
      style="color: var(--text-color)"
    >
      Snippets
    </h2>

    <TabView v-model:activeIndex="activeTabIndex">
      <TabPanel header="Library">
        <Card>
          <template #header>
            <div
              class="p-3 pb-0 flex flex-column sm:flex-row justify-content-between gap-3"
            >
              <Tag
                v-if="libraryShowFilter"
                severity="info"
                class="cursor-pointer"
                @click="clearLibraryShowFilter"
              >
                Show: {{ libraryShowFilter.show_title }}
                <i class="pi pi-times ml-2"></i>
              </Tag>
              <span v-else></span>
              <InputText
                class="w-full sm:w-24rem"
                type="text"
                v-model="searchQuery"
                @input="onSearchInput"
                placeholder="Search show or episode"
              />
            </div>
          </template>
          <template #content>
            <DataTable
              :value="snippets"
              class="p-datatable-sm"
              lazy
              :paginator="true"
              :rows="10"
              :total-records="totalSnippets"
              :loading="loading"
              @page="onPage"
            >
              <Column field="id" header="ID">
                <template #body="slotProps">
                  <span class="text-500 text-sm">{{ slotProps.data.id }}</span>
                </template>
              </Column>

              <Column field="show_title" header="Show" />

              <Column field="episode_title" header="Episode">
                <template #body="slotProps">
                  <span
                    class="episode-title-cell"
                    v-tooltip.top="slotProps.data.episode_title"
                  >
                    {{ slotProps.data.episode_title }}
                  </span>
                </template>
              </Column>

              <Column header="Audio">
                <template #body="slotProps">
                  <audio
                    controls
                    preload="none"
                    class="snippet-audio-player"
                    :src="slotProps.data.snippet_audio_url"
                  />
                </template>
              </Column>

              <Column header="Transcript">
                <template #body="slotProps">
                  <Button
                    v-if="slotProps.data.metadata?.transcript_text"
                    label="View"
                    size="small"
                    text
                    @click="openTranscript(slotProps.data)"
                  />
                  <span v-else class="text-500 text-sm">None</span>
                </template>
              </Column>

              <Column field="created_at" header="Created At">
                <template #body="slotProps">
                  {{ formatDate(slotProps.data.created_at) }}
                </template>
              </Column>
            </DataTable>
          </template>
        </Card>
      </TabPanel>

      <TabPanel header="Shows">
        <Card>
          <template #content>
            <DataTable
              :value="shows"
              class="p-datatable-sm"
              :paginator="true"
              :rows="10"
              :loading="showsLoading"
            >
              <template #header>
                <div class="flex justify-content-end">
                  <Button
                    label="Add Show"
                    icon="pi pi-plus"
                    size="small"
                    @click="openAddShowDialog"
                  />
                </div>
              </template>

              <Column field="show_title" header="Show" :sortable="true" />

              <Column
                field="snippet_count"
                header="Snippets Generated"
                :sortable="true"
              >
                <template #body="slotProps">
                  <Badge
                    :severity="
                      slotProps.data.snippet_count > 0 ? 'success' : 'secondary'
                    "
                    :value="slotProps.data.snippet_count"
                  />
                </template>
              </Column>

              <Column
                field="last_snippet_at"
                header="Last Snippet Generated"
                :sortable="true"
              >
                <template #body="slotProps">
                  <span v-if="slotProps.data.last_snippet_at">
                    {{ formatDate(slotProps.data.last_snippet_at) }}
                  </span>
                  <span v-else class="text-500 text-sm">Never</span>
                </template>
              </Column>

              <Column
                field="active_seller_count"
                header="Active Sellers"
                :sortable="true"
              >
                <template #body="slotProps">
                  <Badge
                    :severity="
                      slotProps.data.active_seller_count > 0
                        ? 'info'
                        : 'secondary'
                    "
                    :value="slotProps.data.active_seller_count"
                  />
                </template>
              </Column>

              <Column>
                <template #body="slotProps">
                  <Button
                    icon="pi pi-eye"
                    v-tooltip.left="
                      slotProps.data.snippet_count > 0
                        ? 'View Snippets'
                        : 'No snippets generated yet'
                    "
                    outlined
                    rounded
                    size="small"
                    class="mr-2"
                    :disabled="slotProps.data.snippet_count === 0"
                    @click="viewSnippetsForShow(slotProps.data)"
                    aria-label="View Snippets"
                  />
                  <Button
                    icon="pi pi-plus"
                    v-tooltip.left="'Generate One More Snippet'"
                    outlined
                    rounded
                    severity="success"
                    size="small"
                    class="mr-2"
                    :loading="generatingOneMoreShowId === slotProps.data.show_id"
                    @click="confirmGenerateOneMoreSnippet(slotProps.data)"
                    aria-label="Generate One More Snippet"
                  />
                  <Button
                    icon="pi pi-trash"
                    v-tooltip.left="
                      slotProps.data.active_seller_count > 0
                        ? 'Cannot remove: sellers still active for this show'
                        : 'Remove Show'
                    "
                    outlined
                    rounded
                    severity="danger"
                    size="small"
                    :disabled="slotProps.data.active_seller_count > 0"
                    @click="confirmRemoveShow(slotProps.data)"
                    aria-label="Remove Show"
                  />
                </template>
              </Column>
            </DataTable>
          </template>
        </Card>
      </TabPanel>
    </TabView>

    <TranscriptDialog
      v-model:visible="showTranscriptDialog"
      :text="selectedTranscript"
    />

    <Dialog
      v-model:visible="showAddShowDialog"
      header="Add Show"
      modal
      :style="{ width: '30rem' }"
    >
      <div class="flex flex-column gap-1">
        <label for="addShowDropdown">Show</label>
        <Dropdown
          id="addShowDropdown"
          v-model="showToAdd"
          :options="availableShowsToAdd"
          optionLabel="show_title"
          filter
          placeholder="Select a show"
          class="w-full"
          :panelStyle="{ width: 'min(26rem, 96vw)', maxHeight: '16rem', overflow: 'hidden' }"
          :loading="availableShowsLoading"
        />
      </div>
      <template #footer>
        <Button
          label="Cancel"
          outlined
          :disabled="addShowSubmitting"
          @click="showAddShowDialog = false"
        />
        <Button
          label="Add"
          :loading="addShowSubmitting"
          :disabled="!showToAdd"
          @click="confirmAddShow"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import { ref, onMounted, watch } from "vue";
import axios from "axios";
import { API_BASE_URL } from "../consts";
import Card from "primevue/card";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import Badge from "primevue/badge";
import Tag from "primevue/tag";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { formatDate } from "../helpers";
import TranscriptDialog from "./TranscriptDialog.vue";

export default {
  components: {
    Card,
    DataTable,
    Column,
    Button,
    Dialog,
    InputText,
    Dropdown,
    Badge,
    Tag,
    TabView,
    TabPanel,
    TranscriptDialog,
  },
  setup() {
    const confirm = useConfirm();
    const toast = useToast();
    const token = localStorage.getItem("adminToken");

    const snippets = ref([]);
    const totalSnippets = ref(0);
    const currentPage = ref(1);
    const searchQuery = ref("");
    let searchDebounceTimer = null;
    const loading = ref(false);

    const activeTabIndex = ref(0);
    const libraryShowFilter = ref(null);

    const viewSnippetsForShow = (show) => {
      libraryShowFilter.value = {
        show_id: show.show_id,
        show_title: show.show_title,
      };
      currentPage.value = 1;
      activeTabIndex.value = 0;
    };

    const clearLibraryShowFilter = () => {
      libraryShowFilter.value = null;
      currentPage.value = 1;
      fetchSnippets();
    };

    const showTranscriptDialog = ref(false);
    const selectedTranscript = ref("");

    const openTranscript = (snippet) => {
      selectedTranscript.value = snippet.metadata?.transcript_text || "";
      showTranscriptDialog.value = true;
    };

    const fetchSnippets = async () => {
      try {
        loading.value = true;
        const response = await axios.get(`${API_BASE_URL}/snippets`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            page: currentPage.value,
            search: searchQuery.value || undefined,
            showId: libraryShowFilter.value?.show_id || undefined,
          },
        });
        if (response.data?.success) {
          snippets.value = response.data.data.snippets;
          totalSnippets.value = response.data.data.total;
        }
      } catch (error) {
        console.error("Error fetching snippets:", error);
      } finally {
        loading.value = false;
      }
    };

    const onPage = (event) => {
      currentPage.value = event.page + 1;
      fetchSnippets();
    };

    const onSearchInput = () => {
      clearTimeout(searchDebounceTimer);
      searchDebounceTimer = setTimeout(() => {
        currentPage.value = 1;
        fetchSnippets();
      }, 400);
    };

    const shows = ref([]);
    const showsLoading = ref(false);

    const fetchShows = async () => {
      try {
        showsLoading.value = true;
        const response = await axios.get(`${API_BASE_URL}/podcast-shows`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { snippetsOnly: true, activeSellerCount: true },
        });
        shows.value = response.data || [];
      } catch (error) {
        console.error("Error fetching snippet shows:", error);
      } finally {
        showsLoading.value = false;
      }
    };

    const removeShow = async (show) => {
      try {
        await axios.delete(`${API_BASE_URL}/snippets/shows/${show.show_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        shows.value = shows.value.filter((s) => s.show_id !== show.show_id);
        toast.add({
          severity: "success",
          summary: "Removed",
          detail: "Show removed from the snippets catalog.",
          life: 4000,
        });
      } catch (error) {
        console.error("Error removing snippet show:", error);
        toast.add({
          severity: "error",
          summary: "Error",
          detail: error.response?.data?.message || "Failed to remove show.",
          life: 4000,
        });
      }
    };

    const confirmRemoveShow = (show) => {
      confirm.require({
        message: `Remove "${show.show_title}" from the snippets catalog?`,
        header: "Remove Confirmation",
        icon: "pi pi-info-circle",
        acceptClass: "p-button-danger",
        accept: () => removeShow(show),
      });
    };

    const generatingOneMoreShowId = ref(null);

    const generateOneMoreSnippet = async (show) => {
      generatingOneMoreShowId.value = show.show_id;
      try {
        await axios.post(
          `${API_BASE_URL}/snippets/shows/${show.show_id}/generate-one`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.add({
          severity: "success",
          summary: "Queued",
          detail: "Snippet generation queued for this show.",
          life: 4000,
        });
      } catch (error) {
        console.error("Error generating one more snippet:", error);
        toast.add({
          severity: "error",
          summary: "Error",
          detail:
            error.response?.data?.message || "Failed to generate snippet.",
          life: 4000,
        });
      } finally {
        generatingOneMoreShowId.value = null;
      }
    };

    const confirmGenerateOneMoreSnippet = (show) => {
      confirm.require({
        message: `Are you sure you want to generate one more snippet for this show?`,
        header: "Generate Confirmation",
        icon: "pi pi-info-circle",
        accept: () => generateOneMoreSnippet(show),
      });
    };

    const showAddShowDialog = ref(false);
    const availableShowsToAdd = ref([]);
    const availableShowsLoading = ref(false);
    const showToAdd = ref(null);
    const addShowSubmitting = ref(false);

    const openAddShowDialog = async () => {
      showToAdd.value = null;
      showAddShowDialog.value = true;
      availableShowsLoading.value = true;

      try {
        const response = await axios.get(`${API_BASE_URL}/podcast-shows`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const enabledShowIds = new Set(shows.value.map((s) => s.show_id));
        availableShowsToAdd.value = (response.data || []).filter(
          (show) => !enabledShowIds.has(show.show_id)
        );
      } catch (error) {
        console.error("Error fetching podcast shows:", error);
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Failed to load shows.",
          life: 4000,
        });
      } finally {
        availableShowsLoading.value = false;
      }
    };

    const confirmAddShow = async () => {
      if (!showToAdd.value) return;

      try {
        addShowSubmitting.value = true;
        await axios.post(
          `${API_BASE_URL}/snippets/shows`,
          { showId: showToAdd.value.show_id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.add({
          severity: "success",
          summary: "Added",
          detail:
            "Show added to the snippets catalog. Generation for its 3 newest episodes has been queued.",
          life: 5000,
        });
        showAddShowDialog.value = false;
        await fetchShows();
      } catch (error) {
        console.error("Error adding snippet show:", error);
        toast.add({
          severity: "error",
          summary: "Error",
          detail: error.response?.data?.message || "Failed to add show.",
          life: 4000,
        });
      } finally {
        addShowSubmitting.value = false;
      }
    };

    watch(activeTabIndex, (index) => {
      if (index === 0) fetchSnippets();
      else if (index === 1) fetchShows();
    });

    onMounted(() => {
      if (activeTabIndex.value === 0) fetchSnippets();
      else fetchShows();
    });

    return {
      snippets,
      totalSnippets,
      searchQuery,
      onSearchInput,
      onPage,
      loading,
      activeTabIndex,
      libraryShowFilter,
      viewSnippetsForShow,
      clearLibraryShowFilter,
      showTranscriptDialog,
      selectedTranscript,
      openTranscript,
      formatDate,
      shows,
      showsLoading,
      confirmRemoveShow,
      generatingOneMoreShowId,
      confirmGenerateOneMoreSnippet,
      showAddShowDialog,
      availableShowsToAdd,
      availableShowsLoading,
      showToAdd,
      addShowSubmitting,
      openAddShowDialog,
      confirmAddShow,
    };
  },
};
</script>

<style scoped>
.snippet-audio-player {
  height: 2.25rem;
  max-width: 16rem;
}

.episode-title-cell {
  display: inline-block;
  max-width: 14rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}
</style>
